import type { WbsClient } from '@bldsoft/cappie/api/wbs'
import type { API, AppearanceBanner } from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'

import { payload } from '@bldsoft/cappie/rest'
import * as v from 'valibot'
import { PLATFORM } from '../../index.h'
import { GenericSliceInfoSchema } from '../../shared/schemas'
import { formatBanners } from '../banners/banners.format'
// --- old formatters helpers, TODO: refactor this
import { formatContinueWatchingItem } from '../continue-watching/continueWatching.format'
import { formatMotnTvShow } from '../external-content/motn-tv-show.format'
import { formatMotnVod } from '../external-content/motn-vod.format'
import { formatLiveEvent } from '../live-events/live-events.format'
import { formatPartnerProduct } from '../partnerProgram/partnerProgram.format'
import { formatShowBundle } from '../showBundles/showBundles.format'
import { formatChannel } from '../tv/tv.format'
import { formatTvBundle } from '../tvBundles/tvBundles.format'
import { formatShow } from '../tvshows/tvshows.format'
import { formatVodBundle } from '../vodBundles/vodBundles.format'
import { formatVod } from '../vods/vods.format'
import { AppearanceSchema, AppearanceSchemaStyles, RowsPageSchema } from './schemas' // prettier-ignore
// ---

/**
 * Row and banner resources are relative paths chosen by the backend,
 * so they can only be requested through the raw escape hatch
 */
function resource(path: string, search?: URLSearchParams): string {
  const query = search?.toString()
  return PLATFORM + path + (query ? `?${query}` : '')
}

export function use(
  client: WbsClient,
  appearance: API['appearance'],
  _api: API,
  _defaults: AdapterDefaults
): void {
  // GET /v3/appearance
  appearance.getAppearanceFx.use(async () => {
    const json = await payload(
      client.get.api.v3.appearance({ query: { 'device-type': 'WEB' } })
    )
    return v.parse(AppearanceSchema, json)
  })

  appearance.getAppearanceStylesFx.use(async () => {
    const json = await payload(
      client.get.api.v3.appearance({ query: { 'device-type': 'WEB' } })
    )
    return v.parse(AppearanceSchemaStyles, json)
  })

  // GET /v3/appearance/modules/{identifier}/rows
  appearance.getModuleComponentsFx.use(
    async (args: { identifier: string; page?: number; count?: number }) => {
      const json = await payload(
        client.get.api.v3.appearance.modules(args.identifier).rows({
          query: { page: args?.page, count: args?.count },
        })
      )
      return v.parse(RowsPageSchema, json)
    }
  )

  // GET content for Row component on dynamic page
  appearance.getRowContentFx.use(async (row) => {
    let fetched: unknown

    // fallback to old wbs, if defined
    if (row.resource.fallback) {
      try {
        const json: any = await row.resource.fallback()
        fetched = json?.payload
      } catch (error) {
        throw new Error(`Row resource fallback failed: ${error}`)
      }
    }

    // fetch row resource from new wbs api, if no fallback defined
    else {
      const searchParams = new URLSearchParams(row.resource.search)

      if (row.paged) {
        const count = row.style.topContent ? '10' : '36'
        searchParams.set('page', '0')
        searchParams.set('count', count)
      }

      fetched = await payload(client.request.get(resource(row.resource.path, searchParams))) // prettier-ignore
    }

    const data = fetched
    const isDataPaged = v.is(GenericSliceInfoSchema, data)

    let items: any[]
    let last: boolean

    if (row.paged) {
      if (!isDataPaged) {
        throw new Error(
          'Invalid row resource response, page or slice info expected'
        )
      }

      items = data.content
      last = data.last
    } else {
      if (isDataPaged) {
        throw new Error(
          'Invalid row resource response, page or slice info was unexpected'
        )
      }
      if (!Array.isArray(data)) {
        throw new Error('Invalid row resource response, array expected')
      }

      items = data
      last = true
    }

    items = items.map((item) => {
      switch (row.contentType) {
        case 'LIVE_EVENT':
          return formatLiveEvent(item) // v.parse(LiveEventItemSchema, item)
        case 'MIXED':
          return formatContinueWatchingItem(item) // return v.parse(MixedItemSchema, item)
        case 'PARTNER_PRODUCT':
          return formatPartnerProduct(item) // v.parse(PartnerProductItemSchema, item)
        case 'TV_BUNDLE':
          return formatTvBundle(item) // v.parse(ChannelsBundleItemSchema, item)
        case 'TV_CHANNEL':
          return formatChannel(item) // v.parse(ChannelItemSchema, item)
        case 'TV_SHOW_BUNDLE':
          return formatShowBundle(item) // v.parse(TvShowsBundleItemSchema, item)
        case 'TV_SHOW':
          return formatShow(item) // v.parse(TvShowItemSchema, item)
        case 'VOD_BUNDLE':
          return formatVodBundle(item) // v.parse(VodsBundleItemSchema, item)
        case 'VOD':
          return formatVod(item) // v.parse(VodItemSchema, item)
        case 'MOTN_VOD':
          return formatMotnVod(item)
        case 'MOTN_TV_SHOW':
          return formatMotnTvShow(item)
      }
    })

    // can show "see all" button only for partner products and live events for now
    // so reset last flag to `true` (no "see all" button) for other content types
    if (
      !last &&
      row.contentType !== 'PARTNER_PRODUCT' &&
      row.contentType !== 'LIVE_EVENT'
    ) {
      last = true
    }

    return {
      content: items.slice(0, 36), // crop non-paged items to 36
      last,
    }
  })

  // GET content for Banner component on dynamic page
  appearance.getBannerContentFx.use(async (comp: AppearanceBanner) => {
    let fetched: unknown

    if (comp.resource.fallback) {
      try {
        const json: any = await comp.resource.fallback() // fallback to old wbs, if defined
        fetched = json?.payload
      } catch (error) {
        throw new Error(`Banner resource fallback failed: ${error}`)
      }
    } else {
      fetched = await payload(client.request.get(resource(comp.resource.path)))
    }

    const data = fetched

    if (!Array.isArray(data)) {
      throw new Error('Invalid banners resource response, array expected')
    }

    const emptyResponse = {
      content: undefined,
      last: false,
    }

    if (!data.length) {
      return emptyResponse
    }

    const bannersEntity = data[0]
    const bannerBundles = bannersEntity?.bannerBundles

    if (!bannerBundles) {
      return emptyResponse
    }

    return {
      content: formatBanners(bannersEntity),
      last: true,
    }
  })
}
